'use client'

import type { Angebote } from 'src/payload-types'

import React from 'react'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { TypedLocale } from 'payload'
import Link from 'next/link'
import Image from 'next/image'
import { imgproxyEnabled, imgproxyLoader } from '@/utilities/imgproxy'

type Props = {
  title: string
  offers: Angebote[]
  locale: TypedLocale
}

export const OffersBlockClient: React.FC<Props> = ({ title, offers, locale }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const offerUrl = (slug: string) => `/${locale}/${locale === 'de' ? 'angebote' : 'offers'}/${slug}`

  return (
    <section className="bg-stone-100 py-12 md:py-24">
      <div className="container mx-auto px-6">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={titleVariants}
          className="mb-4 text-center md:mb-16"
        >
          <AnimatedText
            text={title}
            type="h2"
            variant="simple"
            classNameText="text-2xl md:text-4xl"
            className=" font-heading text-2xl font-medium text-gray-900 md:text-4xl"
            delay={0.2}
          />
        </motion.div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6"
        >
          {offers.map((offer) => (
            <Link
              key={offer.id}
              href={
                offer.slug
                  ? offerUrl(offer.slug)
                  : `/${locale}/${locale === 'de' ? 'angebote' : 'offers'}`
              }
              aria-label={offer.angebotTitle}
              className="block"
            >
              <motion.div
                variants={cardVariants}
                className="group relative aspect-[4/3] overflow-hidden md:aspect-[3/2]"
              >
                <Image
                  loader={imgproxyEnabled ? imgproxyLoader : undefined}
                  src={getMediaUrl(offer.image)}
                  alt={offer.angebotTitle}
                  width={800}
                  height={600}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-4 p-6">
                  <div>
                    <h3 className="font-heading text-lg font-medium text-white md:text-xl">
                      {offer.angebotTitle}
                    </h3>
                    {offer.timeline && (
                      <p className="mt-1 text-sm font-light text-white/90 md:text-base">
                        {offer.timeline}
                      </p>
                    )}
                  </div>
                  <span className="inline-block w-fit  bg-antonie-green px-6 py-3 font-heading text-sm font-medium uppercase tracking-widest text-gray-900 transition-colors hover:bg-antonie-green-hover">
                    {locale === 'de' ? 'Zum Angebot' : 'To the offer'}
                  </span>
                </div>
              </motion.div>
            </Link>
          ))}
        </motion.div>

        <motion.div
          variants={buttonVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="mt-12 text-center md:mt-16"
        >
          <Link href={`/${locale}/${locale === 'de' ? 'angebote' : 'offers'}`}>
            <span className="inline-block  border-2 border-antonie-green px-8 py-3 font-heading text-sm font-medium uppercase tracking-widest text-black transition-colors hover:bg-antonie-green hover:text-black">
              {locale === 'de' ? 'Alle Angebote Anzeigen' : 'Show all offers'}
            </span>
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
